import { useState, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Loader2, Copy, Check, Eye, EyeOff, ExternalLink } from 'lucide-react'
import { useLineConfig } from './useLineConfig'

// LINE icon SVG (official brand mark, simplified)
function LineIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
    </svg>
  )
}

type StatusType = 'not_configured' | 'configured' | 'enabled'

function getStatus(
  channelId: string,
  hasSecret: boolean,
  enabled: boolean
): StatusType {
  if (!channelId && !hasSecret) return 'not_configured'
  if (channelId && hasSecret && !enabled) return 'configured'
  if (enabled) return 'enabled'
  return 'not_configured'
}

const STATUS_CONFIG: Record<StatusType, { label: string; className: string }> = {
  not_configured: {
    label: 'Not configured',
    className: 'bg-gray-100 text-gray-600 border-0',
  },
  configured: {
    label: 'Configured',
    className: 'bg-amber-50 text-amber-700 border-0',
  },
  enabled: {
    label: 'Enabled',
    className: 'bg-green-50 text-green-700 border-0',
  },
}

interface LineLoginSettingsProps {
  storefrontUrl: string
}

export function LineLoginSettings({ storefrontUrl }: LineLoginSettingsProps) {
  const { config, isLoading, save, isSaving, saveError } = useLineConfig()

  const [channelId, setChannelId] = useState('')
  const [channelSecret, setChannelSecret] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [showSecret, setShowSecret] = useState(false)
  const [copied, setCopied] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [initialized, setInitialized] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Sync form state from server config on first load
  if (config && !initialized) {
    setChannelId(config.line_channel_id || '')
    setEnabled(config.line_login_enabled)
    setInitialized(true)
  }

  const handleCopy = useCallback((value: string) => {
    navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [])

  const handleToggle = useCallback((checked: boolean) => {
    if (checked) {
      const newErrors: Record<string, string> = {}
      if (!channelId.trim()) {
        newErrors.channelId = 'Channel ID is required to enable LINE login.'
      }
      if (!channelSecret && !config?.has_channel_secret) {
        newErrors.channelSecret = 'Channel Secret is required to enable LINE login.'
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        return
      }
    }
    setErrors({})
    setEnabled(checked)
  }, [channelId, channelSecret, config?.has_channel_secret])

  const handleSave = useCallback(() => {
    setErrors({})
    setSaveSuccess(false)
    save(
      {
        line_login_enabled: enabled,
        line_channel_id: channelId,
        line_channel_secret: channelSecret || undefined,
      },
      {
        onSuccess: () => {
          setChannelSecret('')
          setSaveSuccess(true)
          setTimeout(() => setSaveSuccess(false), 1500)
        },
      }
    )
  }, [enabled, channelId, channelSecret, save])

  if (isLoading) {
    return (
      <Card className="rounded-2xl border-0">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  const status = getStatus(
    channelId || config?.line_channel_id || '',
    !!(channelSecret || config?.has_channel_secret),
    enabled
  )
  const statusCfg = STATUS_CONFIG[status]

  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LineIcon className="w-4 h-4 text-[#06C755]" />
            <span className="font-semibold">LINE Login</span>
          </div>
          <Badge
            className={statusCfg.className}
            aria-label={`LINE Login status: ${statusCfg.label}`}
          >
            {statusCfg.label}
          </Badge>
        </div>

        {/* Toggle */}
        <div className="flex items-center justify-between">
          <Label htmlFor="line-login-toggle" className="text-sm">
            Enable LINE Login
          </Label>
          <Switch
            id="line-login-toggle"
            checked={enabled}
            onCheckedChange={handleToggle}
            disabled={isSaving}
            aria-label="Enable LINE Login"
          />
        </div>

        {/* Channel ID */}
        <div className="space-y-1.5">
          <Label htmlFor="line-channel-id" className="text-sm">
            Channel ID
          </Label>
          <Input
            id="line-channel-id"
            type="text"
            placeholder="1234567890"
            value={channelId}
            onChange={(e) => {
              setChannelId(e.target.value)
              if (errors.channelId) {
                setErrors((prev) => {
                  const next = { ...prev }
                  delete next.channelId
                  return next
                })
              }
            }}
            disabled={isSaving}
            className={`rounded ${errors.channelId ? 'border-red-500' : ''}`}
          />
          {errors.channelId && (
            <p className="text-xs text-red-600" role="alert">
              {errors.channelId}
            </p>
          )}
        </div>

        {/* Channel Secret */}
        <div className="space-y-1.5">
          <Label htmlFor="line-channel-secret" className="text-sm">
            Channel Secret
          </Label>
          <div className="relative">
            <Input
              id="line-channel-secret"
              type={showSecret ? 'text' : 'password'}
              placeholder={config?.has_channel_secret ? '••••••••••••••••' : 'Enter channel secret'}
              value={channelSecret}
              onChange={(e) => {
                setChannelSecret(e.target.value)
                if (errors.channelSecret) {
                  setErrors((prev) => {
                    const next = { ...prev }
                    delete next.channelSecret
                    return next
                  })
                }
              }}
              disabled={isSaving}
              className={`rounded pr-10 ${errors.channelSecret ? 'border-red-500' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowSecret(!showSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showSecret ? 'Hide password' : 'Show password'}
            >
              {showSecret ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          {errors.channelSecret && (
            <p className="text-xs text-red-600" role="alert">
              {errors.channelSecret}
            </p>
          )}
        </div>

        {/* Callback URL — always visible, built from tenant's storefront URL */}
        <div className="space-y-1.5">
          <Label className="text-sm">Callback URL</Label>
          <p className="text-xs text-muted-foreground">
            Copy this URL and paste it in your LINE channel's redirect settings.
          </p>
          <div className="relative">
            <Input
              readOnly
              value={`${storefrontUrl}/api/auth/line/callback`}
              className="rounded bg-gray-50 pr-10"
            />
            <button
              type="button"
              onClick={() => handleCopy(`${storefrontUrl}/api/auth/line/callback`)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Copy callback URL to clipboard"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>

        {/* Help text */}
        <p className="text-xs text-muted-foreground">
          Get these values from your{' '}
          <a
            href="https://developers.line.biz/console/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline inline-flex items-center gap-0.5"
          >
            LINE Developer Console
            <ExternalLink className="w-3 h-3" />
          </a>
        </p>

        {/* Error message */}
        {saveError && (
          <p className="text-sm text-red-600" role="alert">
            {saveError}
          </p>
        )}

        {/* Save button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="min-w-[80px]"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                Saving...
              </>
            ) : saveSuccess ? (
              <>
                <Check className="w-4 h-4 mr-1.5 text-green-600" />
                Saved
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
